import PropTypes from 'prop-types'
import { withTranslation } from 'server/i18n'
import { Flex, Box } from 'reflexbox'
import { NextSeo } from 'next-seo'
import getConfig from 'next/config'
import { useUIDSeed } from 'react-uid'

import layoutTheme from 'theme/layout-theme'
import PageTitle from 'components/PageTitle'
import BoatMainImage from 'components/BoatMainImage'
import Staff from 'components/Staff'
import BlockTitle from 'components/BlockTitle'
import OurPartners from 'components/OurPartners'
import Testimonials from 'components/Testimonials'
import Article from 'components/Article'
import FaqBox from 'components/FaqBox'
import Button from 'components/Button'

import PhotoGallerySlider from 'components/PhotoGallerySlider'
import { getPage, getTeamMembers, getTestimonials, getPartners } from '../../helpers/wordpress/wp-api'

const { publicRuntimeConfig } = getConfig()
const { CLOUDINARY_URL } = publicRuntimeConfig

const cmsPage = ({ t, data, i18n, policyTermsPage, teamMembers, testimonials, partners }) => {
    const pageData = i18n.language === 'en' ? data : data.translation
    let sliderImages = []
    const uid = useUIDSeed()

    if (pageData?.acf?.slider?.images) {
        sliderImages = pageData?.acf.slider.images.map(item => {
            return { url: item.image.sourceUrl }
        })
    }

    const seoData = {
        title: `${pageData?.seo?.title} | Merk & Merk`,
        description: pageData?.seo?.metaDesc,
        canonical: `https://merkandmerk.${i18n.language === 'en' ? 'com' : 'de'}/${data.slug}`,
        openGraph: {
            title: `${pageData?.seo?.title} | Merk & Merk`,
            description: pageData?.seo?.metaDesc,
            url: `https://merkandmerk.${i18n.language === 'en' ? 'com' : 'de'}/${data.slug}`,
            images: [
                {
                    url: pageData?.featuredImage
                        ? pageData?.featuredImage.node.sourceUrl
                        : `${CLOUDINARY_URL}/v1582632369/Static/sell-image_hace64.jpg`
                }
            ]
        },
        languageAlternates: [
            {
                hrefLang: 'en',
                href: `https://merkandmerk.hr/${pageData?.slug}`
            },
            {
                hrefLang: 'de',
                href: `https://merkandmerk.de/${pageData.slug}`
            }
        ]
    }

    if (policyTermsPage) {
        return (
            <main>
                <NextSeo {...seoData} />
                <Box
                    theme={layoutTheme}
                    px={40}
                    mt={[80, null, null, 140]}
                    mb={[30, null, null, 80]}
                    maxWidth={940}
                    mx="auto">
                    <PageTitle title={pageData?.title} titleTag="h1" />
                </Box>
                <Box theme={layoutTheme} px={40} mb={[100, null, null, 150]} maxWidth={940} mx="auto">
                    <Article content={pageData?.content} />
                </Box>
            </main>
        )
    }

    return (
        <main>
            <NextSeo {...seoData} />
            <Box theme={layoutTheme} px={20} mt={[35, null, 70, 100, 150]} mb={[35, null, 70, 80]} variant="center1280">
                <PageTitle title={pageData?.title} subtitle={pageData?.acf.subtitle} titleTag="h1" subtitleTag="h2" />

                {pageData?.acf.headerText.text !== '' && pageData?.acf.headerText.button && (
                    <Flex
                        theme={layoutTheme}
                        justifyContent="space-between"
                        flexDirection={['column', null, null, 'row']}
                        mt={[20, null, null, 40]}
                        alignItems={['flex-start', null, null, 'center']}>
                        <Box
                            theme={layoutTheme}
                            mb={[20, null, null, 0]}
                            fontSize={[14, null, 16, 20]}
                            maxWidth={[null, null, null, '70%', '80%']}>
                            {pageData?.acf.headerText.text}
                        </Box>
                        <Button variation="tertiary bigOnLarge" link="/inquiry">
                            {pageData?.acf.headerText.button}
                        </Button>
                    </Flex>
                )}
            </Box>

            {pageData?.featuredImage && (
                <Box theme={layoutTheme} px={20} mb={[70, null, null, 130]} maxWidth={1600} mx="auto">
                    <BoatMainImage
                        image={pageData?.featuredImage.node.sourceUrl}
                        alt={pageData?.featuredImage.node.altText}
                    />
                </Box>
            )}

            {pageData?.content && (
                <Box theme={layoutTheme} px={20} mb={[55, null, null, 260]} maxWidth={1040} mx="auto">
                    <Article content={pageData?.content} />
                </Box>
            )}

            {sliderImages.length > 0 && (
                <>
                    <Box theme={layoutTheme} mb={[0, null, null, 40]} maxWidth={1320} mx="auto">
                        <Box theme={layoutTheme} mb={[0, 0, 0, 60]} px={40}>
                            <BlockTitle
                                title={pageData?.acf.slider.title}
                                subtitle={pageData?.acf.slider.subtitle}
                                paddingLeft={0}
                            />
                        </Box>
                    </Box>

                    <Box theme={layoutTheme} mb={[70, null, 100, 200]}>
                        <PhotoGallerySlider images={sliderImages} />
                    </Box>
                </>
            )}

            {pageData?.acf.showTestimonials && (
                <Box theme={layoutTheme} variant="center1360" px={40} mt={[0, 0, 80, 140]} mb={[100, null, 100, 215]}>
                    <Testimonials
                        testimonials={testimonials}
                        title={t('what-they-say')}
                        subtitle={t('hopefully-good-stuff')}
                    />
                </Box>
            )}

            {pageData?.acf.showTeamMembers && (
                <Box theme={layoutTheme} mb={[0, null, null, 40]} maxWidth={1320} mx="auto">
                    <Box theme={layoutTheme} mb={[0, 0, 0, 60]} px={40}>
                        <BlockTitle title={t('our-team-title')} subtitle={t('our-team-subtitle')} paddingLeft={0} />
                    </Box>
                    <Box theme={layoutTheme} mb={[0, 0, 0, 60]} px={20}>
                        <Staff data={teamMembers} columns={4} />
                    </Box>
                </Box>
            )}

            {pageData?.acf.showPartners && (
                <Box theme={layoutTheme} mb={[0, null, null, 190]} mt={[0, null, 0, 150]}>
                    <Box theme={layoutTheme} variant="blockTitle">
                        <BlockTitle title={t('our-partners')} paddingLeft={0} />
                    </Box>
                    <OurPartners partners={partners} />
                </Box>
            )}

            {pageData?.acf.faqs && (
                <Box theme={layoutTheme} mb={(80, null, null, 200)} px={20} maxWidth={960} mx="auto">
                    {pageData?.acf.faqs.map(item => (
                        <FaqBox key={uid(item)} question={item.question} answer={item.answer} />
                    ))}
                </Box>
            )}

            {pageData?.acf?.impressum && (
                <Box theme={layoutTheme} mb={(80, null, null, 200)} px={20} maxWidth={960} mx="auto">
                    <Article content={pageData?.acf.impressum.text} />
                </Box>
            )}
        </main>
    )
}

cmsPage.propTypes = {
    data: PropTypes.oneOfType([() => null, PropTypes.object]).isRequired,
    i18n: PropTypes.oneOfType([() => null, PropTypes.object]).isRequired,
    policyTermsPage: PropTypes.bool,
    teamMembers: PropTypes.arrayOf({}),
    testimonials: PropTypes.arrayOf({}),
    partners: PropTypes.arrayOf({}),
    t: PropTypes.func.isRequired
}

cmsPage.defaultProps = {
    policyTermsPage: false,
    teamMembers: null,
    testimonials: null,
    partners: null
}

export async function getServerSideProps({ params }) {
    const data = await getPage(params.slug)
    const teamMembers = await getTeamMembers()
    const testimonials = await getTestimonials()
    const partners = await getPartners()

    const policyTermsPage = params.slug === 'privacy-policy' || params.slug === 'terms-and-conditions'

    return {
        props: {
            data: data.page,
            policyTermsPage,
            teamMembers: teamMembers.teamMembers.nodes,
            testimonials: testimonials.testimonials.nodes,
            partners: partners.partners.nodes
        }
    }
}

export default withTranslation()(cmsPage)
